import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import authService from '../../../services/authService';
import { hasFeatureAccess, hasActionAccess } from '../../../utils/roleBasedAccess';

const QuickActions = () => {
  const navigate = useNavigate();
  
  // Get current user role
  const currentUser = authService.getStoredUser();
  const userRole = currentUser?.role;

  const allQuickActions = [
    {
      id: 1,
      title: 'Upload CVs',
      description: 'Add new candidate CVs for AI screening',
      icon: 'Upload',
      color: 'primary',
      path: '/cv-upload-processing',
      stats: '15 pending',
      requiredFeature: 'cv-processing'
    },
    {
      id: 2,
      title: 'Create Job Posting',
      description: 'Post new job openings',
      icon: 'Briefcase',
      color: 'secondary',
      path: '/job-postings',
      stats: '3 active',
      requiredRoles: ['admin', 'hr_manager', 'recruiter']
    },
    {
      id: 3,
      title: 'Schedule Interview',
      description: 'Book interviews with candidates',
      icon: 'Calendar',
      color: 'success',
      path: '/interview-scheduling',
      stats: '8 today',
      requiredAccess: { type: 'interviews', action: 'schedule' }
    }
  ];

  // Filter actions based on user role
  const quickActions = allQuickActions.filter(action => {
    if (action.requiredFeature) {
      return hasFeatureAccess(userRole, action.requiredFeature);
    }
    if (action.requiredRoles) {
      return action.requiredRoles.includes(userRole);
    }
    if (action.requiredAccess) {
      return hasActionAccess(userRole, action.requiredAccess.type, action.requiredAccess.action);
    }
    return true; // Show action if no restrictions
  });

  const recentTasks = [
    {
      id: 1,
      task: 'Review AI screening results',
      priority: 'high',
      dueTime: '2:00 PM'
    },
    {
      id: 2,
      task: 'Interview with Sarah Johnson',
      priority: 'medium',
      dueTime: '3:30 PM'
    },
    {
      id: 3,
      task: 'Update job requirements',
      priority: 'low',
      dueTime: 'Tomorrow'
    }
  ];

  const handleActionClick = (path) => {
    navigate(path);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'text-destructive bg-destructive/10',
      medium: 'text-warning bg-warning/10',
      low: 'text-muted-foreground bg-muted'
    };
    return colors?.[priority] || colors?.low;
  };

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <div className="bg-card border border-border rounded-lg p-6 card-elevation-base">
        <h3 className="text-lg font-semibold text-foreground mb-6">Quick Actions</h3>
        <div className="space-y-4">
          {quickActions?.map((action) => (
            <Button
              key={action?.id}
              variant="outline"
              fullWidth
              onClick={() => handleActionClick(action?.path)}
              className="h-auto p-4 justify-start"
            >
              <div className="flex items-center space-x-4 w-full">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  action?.color === 'primary' ? 'bg-primary/10 text-primary' :
                  action?.color === 'secondary'? 'bg-secondary/10 text-secondary' : 'bg-success/10 text-success'
                }`}>
                  <Icon name={action?.icon} size={20} />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-foreground">{action?.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{action?.description}</p>
                  <p className="text-xs text-primary mt-1">{action?.stats}</p>
                </div>
                <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
              </div>
            </Button>
          ))}
        </div>
      </div>
      {/* Today's Tasks */}
      <div className="bg-card border border-border rounded-lg p-6 card-elevation-base">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Today's Tasks</h3>
          <span className="text-sm text-muted-foreground">{recentTasks?.length} tasks</span>
        </div>
        
        <div className="space-y-3">
          {recentTasks?.map((task) => (
            <div key={task?.id} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{task?.task}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(task?.priority)}`}>
                    {task?.priority}
                  </span>
                  <span className="text-xs text-muted-foreground">{task?.dueTime}</span>
                </div>
              </div>
              <button className="p-1 hover:bg-muted rounded micro-feedback">
                <Icon name="MoreHorizontal" size={16} className="text-muted-foreground" />
              </button>
            </div>
          ))}
        </div>
        
        <div className="mt-4 pt-4 border-t border-border">
          <Button variant="ghost" fullWidth>
            <Icon name="Plus" size={16} />
            Add New Task
          </Button>
        </div>
      </div>
      {/* AI Insights */}
      <div className="bg-card border border-border rounded-lg p-6 card-elevation-base">
        <div className="flex items-center space-x-2 mb-4">
          <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Brain" size={16} color="var(--color-accent)" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">AI Insights</h3>
        </div>
        
        <div className="space-y-4">
          <div className="p-4 bg-success/5 border border-success/20 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="TrendingUp" size={16} className="text-success" />
              <span className="text-sm font-medium text-success">Screening Efficiency</span>
            </div>
            <p className="text-xs text-muted-foreground">
              AI screening accuracy improved by 12% this week. Consider updating screening criteria for Marketing roles.
            </p>
          </div>
          
          <div className="p-4 bg-warning/5 border border-warning/20 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="AlertTriangle" size={16} className="text-warning" />
              <span className="text-sm font-medium text-warning">Attention Needed</span>
            </div>
            <p className="text-xs text-muted-foreground">
              High volume of applications for Senior Developer role. Consider batch processing.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickActions;